import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../../../firebase";
import {
  DEFAULT_GRAPH_ID,
  getImportsMeta,
  graphEdgesCol,
  graphNodesCol,
  graphNodeDoc,
  updateImportsMeta,
  upsertEdge,
  upsertNode,
} from "../graphStore";

const BASE_TYPE_DEFS = [
  ["type-entity", "Entity", null],
  ["type-person", "Person", "Entity"],
  ["type-organization", "Organization", "Entity"],
  ["type-place", "Place", "Entity"],
  ["type-thing", "Thing", "Entity"],
  ["type-concept", "Concept", "Entity"],
  ["type-event", "Event", "Entity"],
  ["type-animal", "Animal", "Thing"],
  ["type-mammal", "Mammal", "Animal"],
  ["type-processsession", "ProcessSession", "Entity"],
  ["type-toloposogo", "TOLOPOSOGO", "ProcessSession"],
  ["type-stage", "Stage", "Entity"],
  ["type-note", "Note", "Entity"],
  ["type-goal", "Goal", "Concept"],
  ["type-fact", "Fact", "Concept"],
  ["type-option", "Option", "Concept"],
  ["type-action", "Action", "Concept"],
  ["type-decision", "Decision", "Concept"],
];

function slug(value) {
  return `${value || ""}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

async function loadTypeMap(graphId, ownerUid) {
  const snap = await getDocs(query(
    graphNodesCol(graphId),
    where("ownerUid", "==", ownerUid)
  ));
  const map = new Map();
  snap.docs.forEach((row) => {
    const data = row.data();
    if (data?.props?.kind === "Type") {
      map.set((data.label || "").toLowerCase(), row.id);
    }
  });
  return map;
}

export async function createBaseTypeSet(graphId = DEFAULT_GRAPH_ID, ownerUid) {
  const byLabel = await loadTypeMap(graphId, ownerUid);

  for (const [nodeId, label] of BASE_TYPE_DEFS) {
    if (!byLabel.has(label.toLowerCase())) {
      await upsertNode(graphId, nodeId, {
        label,
        props: { kind: "Type" },
        ownerUid,
      });
      byLabel.set(label.toLowerCase(), nodeId);
    }
  }

  for (const [nodeId, label, parent] of BASE_TYPE_DEFS) {
    if (!parent) continue;
    const childId = byLabel.get(label.toLowerCase()) || nodeId;
    const parentId = byLabel.get(parent.toLowerCase());
    if (!parentId) continue;
    const edgeId = `isa-${childId}-${parentId}`;
    await upsertEdge(graphId, edgeId, {
      type: "IS_A",
      from: { nodeId: childId },
      to: { nodeId: parentId },
      props: {},
      ownerUid,
    });
  }

  await updateImportsMeta(graphId, { baseTypesCreated: true });
}

function gridPosition(index, columns = 8, cellX = 150, cellY = 110) {
  return {
    x: 80 + (index % columns) * cellX,
    y: 90 + Math.floor(index / columns) * cellY,
  };
}

async function ensureTypeId(graphId, ownerUid, label, fallbackId) {
  const byLabel = await loadTypeMap(graphId, ownerUid);
  const key = label.toLowerCase();
  if (byLabel.has(key)) return byLabel.get(key);
  const nodeId = fallbackId || `type-${slug(label)}`;
  await upsertNode(graphId, nodeId, {
    label,
    props: { kind: "Type" },
    ownerUid,
  });
  return nodeId;
}

export async function importMilfordIfNeeded(graphId = DEFAULT_GRAPH_ID, ownerUid) {
  const meta = await getImportsMeta(graphId);
  if (meta.milfordImported) return;

  const legacyRef = doc(db, "milford_models", ownerUid);
  const legacySnap = await getDoc(legacyRef);
  if (!legacySnap.exists()) {
    await updateImportsMeta(graphId, { milfordImported: true });
    return;
  }

  await createBaseTypeSet(graphId, ownerUid);
  const personTypeId = await ensureTypeId(graphId, ownerUid, "Person", "type-person");
  const data = legacySnap.data();
  const people = Array.isArray(data.people) ? data.people : [];
  const links = Array.isArray(data.links) ? data.links : [];

  for (let i = 0; i < people.length; i += 1) {
    const person = people[i];
    const nodeId = person.id || `milford-person-${i}`;
    const ui = person?.x != null && person?.y != null
      ? { x: Number(person.x) || 0, y: Number(person.y) || 0 }
      : gridPosition(i, 7);

    await upsertNode(graphId, nodeId, {
      label: person.name || `Person ${i + 1}`,
      props: {
        ...person,
        legacySource: "milford",
      },
      ui,
      ownerUid,
    });

    await upsertEdge(graphId, `hastype-${nodeId}-${personTypeId}`, {
      type: "HAS_TYPE",
      from: { nodeId },
      to: { nodeId: personTypeId },
      props: {},
      ownerUid,
    });
  }

  for (let i = 0; i < links.length; i += 1) {
    const link = links[i];
    const edgeId = link.id || `milford-link-${i}`;
    if (!link.from || !link.to) continue;
    await upsertEdge(graphId, edgeId, {
      type: link.relation || "RELATIONSHIP",
      from: { nodeId: link.from },
      to: { nodeId: link.to },
      props: {
        ...link,
        relation: link.relation || "RELATIONSHIP",
        legacySource: "milford",
      },
      ownerUid,
    });
  }

  await updateImportsMeta(graphId, { milfordImported: true });
}

const TOLO_STAGES = ["TO", "LO", "PO", "SO", "GO"];

function stageColor(stage) {
  const map = {
    TO: "#0f766e",
    LO: "#0369a1",
    PO: "#7c3aed",
    SO: "#c2410c",
    GO: "#15803d",
  };
  return map[stage] || "#334155";
}

async function upsertToloSessionGraph(graphId, ownerUid, sessionId, payload, index = 0) {
  await createBaseTypeSet(graphId, ownerUid);
  const [sessionTypeId, toloTypeId, stageTypeId, noteTypeId] = await Promise.all([
    ensureTypeId(graphId, ownerUid, "ProcessSession", "type-processsession"),
    ensureTypeId(graphId, ownerUid, "TOLOPOSOGO", "type-toloposogo"),
    ensureTypeId(graphId, ownerUid, "Stage", "type-stage"),
    ensureTypeId(graphId, ownerUid, "Note", "type-note"),
  ]);

  const sessionNodeId = `tolo-session-${sessionId}`;
  const responses = payload?.responses && typeof payload.responses === "object" ? payload.responses : {};
  const title = payload?.title || "Untitled Session";

  await upsertNode(graphId, sessionNodeId, {
    label: title,
    props: {
      kind: "ProcessSession",
      title,
      stageIndex: Number(payload?.stageIndex) || 0,
      physicsEnabled: payload?.physicsEnabled !== false,
      responses,
      nodeOverrides: payload?.nodeOverrides || {},
      legacySessionId: sessionId,
      legacySource: "toloposogo_sessions",
    },
    ui: gridPosition(index, 4, 300, 240),
    ownerUid,
  });

  await Promise.all([
    upsertEdge(graphId, `hastype-${sessionNodeId}-${sessionTypeId}`, {
      type: "HAS_TYPE",
      from: { nodeId: sessionNodeId },
      to: { nodeId: sessionTypeId },
      props: {},
      ownerUid,
    }),
    upsertEdge(graphId, `hastype-${sessionNodeId}-${toloTypeId}`, {
      type: "HAS_TYPE",
      from: { nodeId: sessionNodeId },
      to: { nodeId: toloTypeId },
      props: {},
      ownerUid,
    }),
  ]);

  let prevStageId = null;

  for (let stageIdx = 0; stageIdx < TOLO_STAGES.length; stageIdx += 1) {
    const stage = TOLO_STAGES[stageIdx];
    const stageNodeId = `tolo-${sessionId}-stage-${stage.toLowerCase()}`;

    await upsertNode(graphId, stageNodeId, {
      label: stage,
      props: {
        stage,
        color: stageColor(stage),
        kind: "Stage",
        sessionNodeId,
      },
      ui: {
        x: 180 + stageIdx * 170,
        y: 200 + index * 20,
      },
      ownerUid,
    });

    await Promise.all([
      upsertEdge(graphId, `hastype-${stageNodeId}-${stageTypeId}`, {
        type: "HAS_TYPE",
        from: { nodeId: stageNodeId },
        to: { nodeId: stageTypeId },
        props: {},
        ownerUid,
      }),
      upsertEdge(graphId, `insession-stage-${stageNodeId}-${sessionNodeId}`, {
        type: "IN_SESSION",
        from: { nodeId: stageNodeId },
        to: { nodeId: sessionNodeId },
        props: {},
        ownerUid,
      }),
    ]);

    if (prevStageId) {
      await upsertEdge(graphId, `nextstage-${prevStageId}-${stageNodeId}`, {
        type: "NEXT_STAGE",
        from: { nodeId: prevStageId },
        to: { nodeId: stageNodeId },
        props: {},
        ownerUid,
      });
    }
    prevStageId = stageNodeId;

    const responseKey = stage.toLowerCase();
    const text = typeof responses[responseKey] === "string" ? responses[responseKey].trim() : "";
    if (text) {
      const noteNodeId = `tolo-${sessionId}-note-${stage.toLowerCase()}`;
      const override = payload?.nodeOverrides?.[responseKey];
      await upsertNode(graphId, noteNodeId, {
        label: `${stage} note`,
        props: {
          text,
          stage,
          kind: "Note",
          sessionNodeId,
        },
        ui: override && Number.isFinite(Number(override.x)) && Number.isFinite(Number(override.y))
          ? { x: Number(override.x), y: Number(override.y) }
          : { x: 180 + stageIdx * 170, y: 320 + index * 18 },
        ownerUid,
      });

      await Promise.all([
        upsertEdge(graphId, `hastype-${noteNodeId}-${noteTypeId}`, {
          type: "HAS_TYPE",
          from: { nodeId: noteNodeId },
          to: { nodeId: noteTypeId },
          props: {},
          ownerUid,
        }),
        upsertEdge(graphId, `hastype-${noteNodeId}-${toloTypeId}`, {
          type: "HAS_TYPE",
          from: { nodeId: noteNodeId },
          to: { nodeId: toloTypeId },
          props: {},
          ownerUid,
        }),
        upsertEdge(graphId, `instage-${noteNodeId}-${stageNodeId}`, {
          type: "IN_STAGE",
          from: { nodeId: noteNodeId },
          to: { nodeId: stageNodeId },
          props: {},
          ownerUid,
        }),
        upsertEdge(graphId, `insession-note-${noteNodeId}-${sessionNodeId}`, {
          type: "IN_SESSION",
          from: { nodeId: noteNodeId },
          to: { nodeId: sessionNodeId },
          props: {},
          ownerUid,
        }),
      ]);
    }
  }

  return sessionNodeId;
}

export async function importTOLOIfNeeded(graphId = DEFAULT_GRAPH_ID, ownerUid) {
  const meta = await getImportsMeta(graphId);
  if (meta.toloImported) return;

  const q = query(collection(db, "toloposogo_sessions"), where("ownerUid", "==", ownerUid));
  const snap = await getDocs(q);
  const rows = snap.docs.map((row) => ({ id: row.id, ...row.data() }));

  for (let i = 0; i < rows.length; i += 1) {
    const session = rows[i];
    await upsertToloSessionGraph(graphId, ownerUid, session.id, session, i);
  }

  await updateImportsMeta(graphId, { toloImported: true });
}

export async function ensureLegacyImports(graphId = DEFAULT_GRAPH_ID, ownerUid) {
  await createBaseTypeSet(graphId, ownerUid);
  await importMilfordIfNeeded(graphId, ownerUid);
  await importTOLOIfNeeded(graphId, ownerUid);
}

export async function getQuiltSessionByLegacyId(graphId = DEFAULT_GRAPH_ID, ownerUid, legacySessionId) {
  const snap = await getDocs(query(
    graphNodesCol(graphId),
    where("ownerUid", "==", ownerUid),
    where("props.legacySessionId", "==", legacySessionId),
    limit(1)
  ));
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
}

export async function upsertTOLOSessionFromPayload(graphId = DEFAULT_GRAPH_ID, ownerUid, sessionId, payload) {
  const sessionNodeId = await upsertToloSessionGraph(graphId, ownerUid, sessionId, payload, 0);
  await setDoc(doc(db, "graphs", graphId, "meta", "imports"), {
    toloImported: true,
    updatedAt: serverTimestamp(),
  }, { merge: true });
  const nodeSnap = await getDoc(graphNodeDoc(graphId, sessionNodeId));
  return nodeSnap.exists() ? { id: nodeSnap.id, ...nodeSnap.data() } : null;
}

export async function listTOLOQuiltSessions(graphId = DEFAULT_GRAPH_ID, ownerUid) {
  const sessionTypeId = await ensureTypeId(graphId, ownerUid, "ProcessSession", "type-processsession");
  const [nodesSnap, edgesSnap] = await Promise.all([
    getDocs(query(graphNodesCol(graphId), where("ownerUid", "==", ownerUid))),
    getDocs(query(graphEdgesCol(graphId), where("ownerUid", "==", ownerUid))),
  ]);
  const nodes = nodesSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
  const typedIds = new Set(
    edgesSnap.docs
      .map((item) => ({ id: item.id, ...item.data() }))
      .filter((edge) => edge.type === "HAS_TYPE" && edge.to?.nodeId === sessionTypeId)
      .map((edge) => edge.from.nodeId)
  );
  return nodes
    .filter((node) => typedIds.has(node.id))
    .sort((a, b) => (b.updatedAt?.toMillis?.() || 0) - (a.updatedAt?.toMillis?.() || 0));
}
